import { useNavigate } from '@tanstack/react-router';
import { PackageOpen, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Separator } from '~/components/ui/separator';
import { CatalogFiltersDrawer } from '~/features/experiments/components/create-experiment/step-2/catalog-filters-drawer';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import {
  ProductsTab,
  type ProductsTabConfig,
} from '~/features/explorer/components/products/products-tab';
import { RevisionSelector } from '~/features/explorer/components/products/revision-selector';
import {
  useProductRevisions,
  useSyncCapabilities,
} from '~/features/explorer/hooks/use-products-catalog';
import {
  useFeedAuditCreationPolicy,
  useFeedAuditProductCapLifted,
} from '~/features/feed-audit/hooks/use-feed-audit-creation-policy';
import { MAX_AUDIT_PRODUCTS } from '~/features/feed-audit/hooks/use-launch-feed-audit';
import { CatalogBatchButton } from '~/features/product-catalog/components/catalog-batch-button';
import { CatalogDetailPanel } from '~/features/product-catalog/components/catalog-detail-panel';
import { CatalogFeedAuditButton } from '~/features/product-catalog/components/catalog-feed-audit-button';
import { CatalogPicker } from '~/features/product-catalog/components/catalog-picker';
import { CatalogResyncButton } from '~/features/product-catalog/components/catalog-resync-button';
import {
  PRODUCT_CATALOG_DEFAULT_COLUMNS,
  PRODUCT_CATALOG_HEADER_SORT,
  PRODUCT_CATALOG_SORT_OPTIONS,
} from '~/features/product-catalog/constants/product-catalog-table';
import {
  useCatalogConnections,
  type CatalogSource,
} from '~/features/product-catalog/hooks/use-catalog-connections';
import { isCatalogResyncVisible } from '~/features/product-catalog/utils/catalog-resync-visibility';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';

/** Table customisation for the catalog view: header sorting, truncated cells,
 *  scrollable body, checkbox column picker, expanded sort list, default columns,
 *  a slide-in detail panel (shared design + catalog quality/issues data), and a
 *  1/2/3/4-column card grid matching the catalog-operations products view. */
const TABLE_CONFIG: ProductsTabConfig = {
  sortOptions: PRODUCT_CATALOG_SORT_OPTIONS,
  headerSortMap: PRODUCT_CATALOG_HEADER_SORT,
  defaultVisibleColumnIds: PRODUCT_CATALOG_DEFAULT_COLUMNS,
  columnPickerVariant: 'checkbox',
  truncateCells: true,
  tableFillHeight: true,
  // Card view: 1 col on mobile → 2 (sm) → 3 (lg) → 4 (xl), matching the
  // catalog-operations products list (products-list-view.tsx).
  cardGridClassName: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  // Server-driven filters (POST products/filters), reusing the experiment
  // creation flow's dynamic drawer; scoped to the selected sync/revision.
  dynamicFilters: true,
  // Customers look products up by identifier: a field picker (offer id, item
  // group id, MPN, GTIN, title) targets the search instead of title-only.
  fieldSearch: true,
  renderFilterSlot: ({ applied, onApply, connectionId, connectorCode, syncId }) => (
    <CatalogFiltersDrawer
      connectorCode={connectorCode}
      connectionId={connectionId}
      applied={applied}
      onApply={onApply}
      syncId={syncId}
      showAppliedCount
    />
  ),
  // Clicking a product opens the catalog detail panel — the shared onboarding
  // panel design (gallery + thumbnails below, highlights, specs, attributes,
  // description, identifiers) plus catalog quality scores, issues and countries.
  renderDetail: ({
    products,
    activeIndex,
    onClose,
    onNavigate,
    connectionId,
    connectorCode,
    subsidiaryId,
  }) => (
    <CatalogDetailPanel
      products={products}
      activeIndex={activeIndex}
      onClose={onClose}
      onNavigate={onNavigate}
      connectionId={connectionId}
      connectorCode={connectorCode}
      subsidiaryId={subsidiaryId}
    />
  ),
};

interface ProductCatalogPageProps {
  /** Selected catalog source from the URL `?source=` param (validated upstream). */
  source: CatalogSource | undefined;
}

/**
 * Product Catalog page: pick an imported catalog (Google / Meta) and browse its
 * products in the reused integrations products table. The selected source is
 * the single source of truth and is mirrored to the URL so the view is
 * reloadable and shareable.
 */
export function ProductCatalogPage({ source }: ProductCatalogPageProps) {
  const { t } = useTranslation('product-catalog');
  const navigate = useNavigate();

  const { catalogs, isLoading } = useCatalogConnections();

  // Resolve the active catalog: honour `?source=` when it matches an available
  // catalog, else default to Google, else the first catalog on hand.
  const selected =
    (source ? catalogs.find((c) => c.source === source) : undefined) ??
    catalogs.find((c) => c.source === 'google') ??
    catalogs[0];
  const resolvedSource = selected?.source;

  // Reconcile the URL to the resolved default when `?source=` is absent or
  // points at a catalog the tenant doesn't have — keeps reload / share honest.
  useEffect(() => {
    if (resolvedSource && resolvedSource !== source) {
      void navigate({ to: '/product-catalog', search: { source: resolvedSource }, replace: true });
    }
  }, [resolvedSource, source, navigate]);

  const connectionId = selected?.connectionId ?? '';
  const connectorCode = selected?.connectorCode ?? '';

  // Revision (point-in-time) scope. `null` = latest. Reset when the catalog
  // changes via a render-time compare (avoids set-state-in-effect).
  const [syncId, setSyncId] = useState<string | null>(null);
  // Applied filters mirrored up from ProductsTab — drives the Meta batch prompt.
  const [appliedFilters, setAppliedFilters] = useState<AppliedProductFilters>({});
  // Server total for the current scope, so the enrichment-run panel can state how
  // many SKUs the batch will cover — including when filters narrow it.
  const [scopedTotal, setScopedTotal] = useState(0);
  // True when the catalog has no products at all (not merely filtered to zero) —
  // hides the filter strip (handled in ProductsTab) and disables Create batch.
  const [catalogEmpty, setCatalogEmpty] = useState(false);
  // True while the products list is still loading — Create batch stays disabled
  // until the catalog resolves. Starts true so it's disabled on first paint.
  const [productsLoading, setProductsLoading] = useState(true);
  const [lastSource, setLastSource] = useState(resolvedSource);
  if (resolvedSource !== lastSource) {
    setLastSource(resolvedSource);
    setSyncId(null);
    setAppliedFilters({});
    setScopedTotal(0);
    setCatalogEmpty(false);
    setProductsLoading(true);
  }

  // Table config + the applied-filters / total callbacks (need component state,
  // so it's memoised here rather than kept as a module constant).
  const tableConfig = useMemo<ProductsTabConfig>(
    () => ({
      ...TABLE_CONFIG,
      onAppliedFiltersChange: (next) => {
        setAppliedFilters(next);
      },
      onTotalCountChange: setScopedTotal,
      onCatalogEmptyChange: setCatalogEmpty,
      onLoadingChange: setProductsLoading,
    }),
    [],
  );

  const { data: revisionsData, isLoading: revisionsLoading } = useProductRevisions(
    { connectionId },
    undefined,
    !!connectionId,
  );
  const revisions = revisionsData?.data ?? [];

  // Effective sync for batch creation: the manually-picked revision, else the
  // latest (revisions are newest-first, so [0]). The catalog-operations flow
  // always sends a concrete syncId, so we resolve "Latest" to its id here
  // rather than omitting it — keeping the /jobs + /trigger payloads identical.
  const effectiveSyncId = syncId ?? revisions[0]?.syncId ?? null;

  const { data: capabilitiesData } = useSyncCapabilities(connectionId, undefined, !!connectionId);
  const capabilities = capabilitiesData?.data ?? [];

  const showEmptyState = !isLoading && catalogs.length === 0;

  // "Run Feed Audit" is offered only for a small enough GMC scope: Feed Audit is
  // GMC-only, run creation is subsidiary/super-admin only (the create/upload
  // routes redirect everyone else), covers at most MAX_AUDIT_PRODUCTS products
  // (unless the user is an admin or `lily-bypass-audit-cap` lifts the cap), and is blocked once the
  // subsidiary has used its single audit (unless the user is a super admin or org admin).
  const canRunAudit = useTenantAdmin();
  const { canCreate: canCreateFeedAudit } = useFeedAuditCreationPolicy();
  const feedAuditCapLifted = useFeedAuditProductCapLifted();
  const canRunFeedAudit =
    canRunAudit &&
    resolvedSource === 'google' &&
    scopedTotal > 0 &&
    (feedAuditCapLifted || scopedTotal <= MAX_AUDIT_PRODUCTS) &&
    canCreateFeedAudit;
  // Whether the resync action applies — same rule the button uses, read here so
  // the banner (and its close button) never render for an empty action set.
  const canResync = isCatalogResyncVisible({
    connectorCode,
    filters: appliedFilters,
    skuCount: scopedTotal,
  });

  // The floating actions banner is dismissible (toast-like) rather than always
  // pinned. Dismissal is scoped to the current scope: a new source/filter/count
  // re-surfaces it via a render-time signature compare, so the actions never
  // stay permanently hidden after the scope changes.
  const [actionsDismissed, setActionsDismissed] = useState(false);
  const actionsSignature = `${resolvedSource ?? ''}|${String(scopedTotal)}|${JSON.stringify(appliedFilters)}`;
  const [lastActionsSignature, setLastActionsSignature] = useState(actionsSignature);
  if (actionsSignature !== lastActionsSignature) {
    setLastActionsSignature(actionsSignature);
    setActionsDismissed(false);
  }

  return (
    <div className="bg-gradient-page h-full overflow-y-auto p-6">
      <div className="mx-auto flex h-full min-h-0 flex-col">
        {/* Header — title + description + catalog / revision selectors. The row
            stays intact down to 980px: between 980–1200px the fixed-width picker
            and revision collapse to icon-only triggers so they still fit beside
            the title; only below 980px does the header stack them under it. */}
        <div className="flex flex-col gap-4 pb-4 min-[980px]:flex-row min-[980px]:flex-wrap min-[980px]:items-start min-[980px]:justify-between">
          <div className="min-w-0">
            <h1 className="text-foreground text-lg font-medium">{t('title')}</h1>
            <p className="text-muted-foreground text-sm">{t('subtitle')}</p>
          </div>
          {!showEmptyState && (
            <div className="flex flex-wrap items-center gap-2">
              <CatalogPicker
                catalogs={catalogs}
                value={resolvedSource}
                onChange={(next) => {
                  void navigate({ to: '/product-catalog', search: { source: next } });
                }}
                isLoading={isLoading}
                triggerClassName="min-[980px]:max-[1199px]:w-auto"
                labelClassName="min-[980px]:max-[1199px]:hidden"
              />
              {!!connectionId && (
                <RevisionSelector
                  revisions={revisions}
                  value={syncId}
                  onChange={setSyncId}
                  isLoading={revisionsLoading}
                  triggerClassName="min-[980px]:max-[1199px]:w-auto"
                  labelClassName="min-[980px]:max-[1199px]:hidden"
                />
              )}
              {/* Always-available batch entry for both Google and Meta — opens a
                  name dialog and starts the platform enrichment flow, scoping to
                  the applied filters when present (whole catalog otherwise). */}
              {!!connectionId && resolvedSource && (
                <CatalogBatchButton
                  source={resolvedSource}
                  connectionId={connectionId}
                  syncId={effectiveSyncId}
                  filters={appliedFilters}
                  skuCount={scopedTotal}
                  disabled={catalogEmpty || productsLoading}
                />
              )}
            </div>
          )}
        </div>

        <Separator />

        {showEmptyState ? (
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
            <PackageOpen className="h-8 w-8 opacity-40" aria-hidden="true" />
            <p className="text-sm font-medium">{t('empty.title')}</p>
            <p className="max-w-md text-sm">{t('empty.description')}</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col pt-4">
            {!!connectionId && (
              // Key by connectionId so switching catalogs remounts the table —
              // its internal filter/search/sort state resets for the new catalog.
              <ProductsTab
                key={connectionId}
                connectionId={connectionId}
                connectorCode={connectorCode}
                syncId={syncId}
                capabilities={capabilities}
                config={tableConfig}
              />
            )}
          </div>
        )}
      </div>

      {/* Dismissible, toast-like banner grouping the scoped catalog actions so
          they read as one group and can be closed rather than staying pinned.
          Each action self-hides when it doesn't apply: Resync when a GMC catalog
          is filtered to ≤50, Run Feed Audit when a GMC scope is ≤20 (super-admin). */}
      {!showEmptyState && !!connectionId && !actionsDismissed && (canResync || canRunFeedAudit) && (
        <div className="bg-popover fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full border py-1.5 pr-1.5 pl-2 shadow-lg">
          <CatalogResyncButton
            connectionId={connectionId}
            connectorCode={connectorCode}
            syncId={effectiveSyncId}
            filters={appliedFilters}
            skuCount={scopedTotal}
          />
          {canRunFeedAudit && (
            <CatalogFeedAuditButton
              syncId={effectiveSyncId}
              filters={appliedFilters}
              skuCount={scopedTotal}
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground h-8 w-8 shrink-0 cursor-pointer rounded-full"
            aria-label={t('floatingActions.dismiss')}
            onClick={() => {
              setActionsDismissed(true);
            }}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
